import React, { useState, useEffect, useCallback } from 'react';
import { q3gRate, q3gWeekKey } from './q3gRates';
import LLPUpdates from './LLPUpdates';
import LineItemLLP from './LineItemLLP';

// Cumulative "midnight → checkpoint" snapshot, styled after the MSM Update card.
// Timeframes are cumulative windows from 00:00; an hour bucket "HH:00" (covering
// [HH:00, HH+1:00)) is included when it STARTS before the window end.
const TIMEFRAMES = [
  { key: '0600', label: '0000-0600', endMin: 6 * 60 },
  { key: '1200', label: '0000-1200', endMin: 12 * 60 },
  { key: '1830', label: '0000-1830', endMin: 18 * 60 + 30 },
  { key: 'full', label: '0000-0000 (Full Day)', endMin: 24 * 60 },
];

// Fixed LVP (LP) plan targets per the mock.
const PLAN = {
  ibCplh: 65.4,
  daCplh: 44.8,
  directCplh: 52.3,
  throughputCplh: 48.2,
  ibDensity: 1.31,
};

// Clock-hour -> BRT (mirrors fclm.js / FastStart schedules). Used to prorate the
// full-day plan to the selected window. Keyed by "HH" so it stays correct
// regardless of the 24hr schedule array ordering (day-then-night vs clock order).
const BRT_BY_HOUR = {
  '08': 45, '09': 60, '10': 45, '11': 60, '12': 40, '13': 30, '14': 60, '15': 60, '16': 40, '17': 60, '18': 25,
  '19': 45, '20': 60, '21': 45, '22': 60, '23': 40, '00': 30, '01': 60, '02': 60, '03': 40, '04': 60, '05': 25,
};

export default function Quarterly({ api, site }) {
  const [data, setData] = useState(null);
  const [lvp, setLvp] = useState(null);
  const [mpWindow, setMpWindow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [tf, setTf] = useState('1200');

  // Sunday of the selected date's week (YYYY-MM-DD). Used by the LVP fetch effect
  // and the Q3G plan-rate lookups below, so it must live in component scope.
  const weekStart = (() => {
    const d = new Date(date + 'T12:00:00');
    const sunday = new Date(d); sunday.setDate(d.getDate() - d.getDay());
    return `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
  })();

  // Full 24h pull so the cumulative-from-midnight windows have every hour.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/api/fclm/hourly?site=${site}&date=${date}&shift_type=24hr`);
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, site, date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // LVP plan for the week (for Combined Cartons plan + IB Pallet FPP plan ratio).
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${weekStart}&shift_type=plan`)
      .then(r => r.json()).then(setLvp).catch(() => setLvp(null));
  }, [api, site, weekStart]);

  // MonitorPortal window cartons (single source, exact 00:00->checkpoint) for the
  // IB Carton Verification — matches the MonitorPortal UI. Refreshed with the tab.
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=MP_WINDOW_${date}&shift_type=data`)
      .then(r => r.json()).then(setMpWindow).catch(() => setMpWindow(null));
  }, [api, site, date, data]);

  const endMin = (TIMEFRAMES.find(t => t.key === tf) || TIMEFRAMES[1]).endMin;
  const inWindow = (label) => {
    const hh = parseInt(String(label).split(':')[0], 10);
    return Number.isFinite(hh) && hh * 60 < endMin;
  };
  const labels = (data?.hour_labels || []).filter(inWindow);

  // Cumulative carton sum for a bucket over the window.
  const cartons = (key) => labels.reduce((s, h) => s + (data?.cartons?.[key]?.hours?.[h] || 0), 0);
  // Cumulative PPR hours for a stream over the window.
  const pprHours = (stream) => labels.reduce((s, h) => s + (data?.ppr_hours?.[stream]?.[h] || 0), 0);

  const ib = cartons('inbound');
  const ob = cartons('outbound');
  const pr = cartons('pr');
  const ibHrs = pprHours('inbound');
  const daHrs = pprHours('transferOut');
  const fcHrs = pprHours('fcSummary'); // all-in throughput hours (incl support/admin)

  // FC Summary throughput extras — read the cumulative-span values PPR itself
  // reports for this checkpoint (fc_span[tf]); per-hour plan data is non-additive.
  const span = data?.fc_span?.[tf] || null;
  const fcVolume = span ? span.volume : 0;
  const ibVolume = span ? span.ibVolume : 0;   // Total Inbound units (PPR homepage)
  const fcPlanHours = span ? span.planHours : 0;   // Hours @ Plan Rate for the span
  const spanActualHours = span ? span.actualHours : 0;
  // % to OP = PPR's own % to Plan for the span (Hours @ Plan Rate / Actual Hours)
  const pctToOp = span && span.pctToPlan ? span.pctToPlan : null;
  // IB Density = IB total units (Total Inbound) / IB cartons (plan fixed 1.31)
  const ibDensityActual = ib > 0 && ibVolume ? ibVolume / ib : null;
  // Density Adjusted % to OP = % to OP / (IB density actual / plan 1.31)
  const densAdjActual = (pctToOp != null && ibDensityActual != null && PLAN.ibDensity > 0)
    ? pctToOp / (ibDensityActual / PLAN.ibDensity) : null;

  const combinedActual = (() => {
    // Combined Cartons = IB, single-source-of-record: MonitorPortal PID + AROS
    // (matching the IB Carton Verification) + FCLM PR + PS. Falls back to raw
    // FCLM inbound if the MP window isn't synced yet.
    const mp = mpWindow?.[tf];
    if (mp && (mp.pid || mp.aros)) return (mp.pid || 0) + (mp.aros || 0) + cartons('pr') + cartons('ps');
    return ib;
  })();
  const fppActual = ib > 0 ? pr / ib : 0;
  const ibCplhActual = ibHrs > 0 ? ib / ibHrs : 0;
  const daCplhActual = daHrs > 0 ? ob / daHrs : 0;
  const directCplhActual = (ibHrs + daHrs) > 0 ? (ib + ob) / (ibHrs + daHrs) : 0;
  const throughputCplhActual = fcHrs > 0 ? (ib + ob) / fcHrs : 0;

  // ---- LVP plan side ----------------------------------------------------
  // Sum the day + night LVP shift cartons for this weekday = full-day plan,
  // then prorate Combined Cartons by the window's share of full-day BRT.
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const wd = dayNames[new Date(date + 'T12:00:00').getDay()];
  const lvpShift = (suffix) => lvp?.shiftData?.[`${wd}_${suffix}`] || null;
  const lvpTotalCartons = (() => {
    const d = lvpShift('day'), n = lvpShift('night');
    return (d?.cartons || 0) + (n?.cartons || 0);
  })();
  const fpp = lvp?.fppRatios || {};
  // LVP PR/IB ratio for the FPP plan (ratio → window-invariant).
  const lvpFppPlan = (() => {
    const total = lvpTotalCartons;
    if (!total) return 0;
    const pid = Math.round(total * (fpp.pid || 0));
    const aros = Math.round(total * (fpp.aros || 0));
    const lvpPr = Math.max(0, total - pid - aros);
    const lvpIb = total; // inbound total ≈ total cartons
    return lvpIb > 0 ? lvpPr / lvpIb : 0;
  })();
  // BRT proration for Combined Cartons plan — sum BRT by CLOCK HOUR so the
  // window share is correct even though the 24hr schedule array is day-then-night.
  const hrBrt = (label) => BRT_BY_HOUR[String(label).split(':')[0]] || 0;
  const allLabels = data?.hour_labels || [];
  const windowBrt = allLabels.reduce((s, h) => s + (inWindow(h) ? hrBrt(h) : 0), 0);
  const fullBrt = allLabels.reduce((s, h) => s + hrBrt(h), 0);
  const brtFrac = fullBrt > 0 ? windowBrt / fullBrt : 0;
  // IB cartons ≈ LVP total cartons; prorate by BRT share of the day.
  const combinedPlan = lvpTotalCartons > 0 ? Math.round(lvpTotalCartons * brtFrac) : 0;

  const titleTime = (TIMEFRAMES.find(t => t.key === tf) || {}).label.replace('0000-', '').replace(' (Full Day)', '');
  const mmdd = (() => { const p = date.split('-'); return `${p[1]}/${p[2]}`; })();

  // ---- Rows -------------------------------------------------------------
  // pct = actual/plan uniformly; >=100% green, <100% red (matches the mock).
  const R = (label, plan, actual, fmt, pending, lowerBetter) => ({ label, plan, actual, fmt, pending, lowerBetter });
  const pctFmt = (v) => `${(v * 100).toFixed(1)}%`;
  const num0 = (v) => Math.round(v).toLocaleString();
  const dec1 = (v) => v.toFixed(1);
  const dec2 = (v) => v.toFixed(2);

  // Effective plan rates: Finance-locked Q3G for the week when LVP is in Q3G mode,
  // Quarterly always uses the Finance-locked Q3G rate for the week (decoupled
  // from the LVP rate-source toggle). Falls back to the fixed LP constants only
  // when the week has no Q3G rates defined. Direct = (IB+OB) / (IB hrs + DA hrs)
  // derived from the IB and DA rates (2 / (1/ib + 1/da)).
  const q3gOn = !!q3gWeekKey(weekStart);
  const planRates = (() => {
    if (!q3gOn) return PLAN;
    const ib = q3gRate('602', weekStart), da = q3gRate('604', weekStart), tp = q3gRate('609', weekStart);
    const direct = ib > 0 && da > 0 ? 2 / (1 / ib + 1 / da) : PLAN.directCplh;
    return { ...PLAN, ibCplh: ib || PLAN.ibCplh, daCplh: da || PLAN.daCplh, directCplh: direct, throughputCplh: tp || PLAN.throughputCplh };
  })();

  const rows = [
    R('Combined Cartons to MSM', combinedPlan, combinedActual, num0),
    R('IB Pallet FPP', lvpFppPlan, fppActual, pctFmt, null, true),
    R(q3gOn ? 'IB CPLH (Q3G)' : 'IB CPLH (LP)', planRates.ibCplh, ibCplhActual, dec1),
    R(q3gOn ? 'DA CPLH (Q3G)' : 'DA CPLH (LP)', planRates.daCplh, daCplhActual, dec1),
    R(q3gOn ? 'CPLH (Excl Support) (Q3G)' : 'CPLH (Excl Support) (LP)', planRates.directCplh, directCplhActual, dec1),
    R(q3gOn ? 'Throughput CPLH (Q3G)' : 'Throughput CPLH (LP)', planRates.throughputCplh, throughputCplhActual, dec1),
    // % to OP is a rate already (actual/plan handled below via plan=1 semantics),
    // so we pass plan=1 and actual=pctToOp to render the PPR % directly.
    R('% to OP', 1, pctToOp, pctFmt),
    R('Density Adjusted % to OP', 1, densAdjActual, pctFmt),
    R('PPR/OP2 Planned Hours', fcPlanHours || null, spanActualHours || fcHrs, num0, null, true),
    R('TNL Last Seven Days', 0, null, dec1, 'external source — pending'),
    R('IB Density', PLAN.ibDensity, ibDensityActual, dec2),
  ];

  // ---- Styling (black rows, yellow outer border, grey inner borders) ----
  const INNER = '2px solid #E7E6E6';
  const wrap = { maxWidth: 640, border: '3px solid #FFFF00', borderRadius: 4, overflow: 'hidden', background: '#000', borderCollapse: 'collapse' };
  const titleCell = { background: '#000', color: '#fff', fontWeight: 700, fontSize: '1.05rem', textAlign: 'center', padding: '10px 8px' };
  // Plan/Actual/% header row: black background (was grey/default)
  const th = { background: '#000', color: '#fff', fontWeight: 700, textAlign: 'right', padding: '4px 14px', fontSize: '0.9rem', border: INNER };
  const labelCell = { background: '#000', color: '#fff', fontWeight: 700, textAlign: 'left', padding: '5px 10px', fontSize: '0.9rem', whiteSpace: 'nowrap', border: INNER };
  const valCell = { background: '#fff', textAlign: 'right', padding: '5px 14px', fontSize: '0.9rem', fontWeight: 600, border: INNER };
  const cellFor = (green) => ({ ...valCell, textAlign: 'right', background: green ? '#c6efce' : '#ffc7ce', color: green ? '#1e6b2f' : '#9c1b2e', fontWeight: 700 });

  const [copyMsg, setCopyMsg] = useState('');

  // Render the table to a canvas (no deps) and copy a PNG to the clipboard.
  const copyTableImage = async () => {
    const disp = rows.map((r) => {
      const hasActual = r.actual != null && !r.pending;
      const hasPlan = r.plan != null;
      const pct = hasActual && hasPlan && r.plan !== 0 ? r.actual / r.plan : null;
      const green = pct != null && (r.lowerBetter ? pct <= 1 : pct >= 1);
      return {
        label: r.label,
        plan: hasPlan ? r.fmt(r.plan) : '—',
        actual: hasActual ? r.fmt(r.actual) : (r.pending ? '⋯' : '—'),
        pct: pct != null ? `${(pct * 100).toFixed(1)}%` : '',
        pctColored: pct != null, green,
      };
    });
    const COLS = [244, 110, 110, 116];         // label, plan, actual, %
    const W = COLS.reduce((a, b) => a + b, 0);
    const TITLE_H = 40, HEAD_H = 30, ROW_H = 30, PAD = 6;
    const H = TITLE_H + HEAD_H + disp.length * ROW_H;
    const dpr = window.devicePixelRatio || 1;
    const cv = document.createElement('canvas');
    cv.width = (W + PAD * 2) * dpr; cv.height = (H + PAD * 2) * dpr;
    const ctx = cv.getContext('2d');
    ctx.scale(dpr, dpr);
    // yellow outer border
    ctx.fillStyle = '#FFFF00'; ctx.fillRect(0, 0, W + PAD * 2, H + PAD * 2);
    const OX = PAD, OY = PAD;
    ctx.textBaseline = 'middle';
    const colX = (i) => OX + COLS.slice(0, i).reduce((a, b) => a + b, 0);
    const drawCell = (x, y, w, h, bg, fg, text, align, bold) => {
      ctx.fillStyle = bg; ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = '#E7E6E6'; ctx.lineWidth = 2; ctx.strokeRect(x, y, w, h);
      if (text) {
        ctx.fillStyle = fg;
        ctx.font = `${bold ? '700' : '600'} 14px Arial, sans-serif`;
        if (align === 'right') { ctx.textAlign = 'right'; ctx.fillText(text, x + w - 10, y + h / 2); }
        else { ctx.textAlign = 'left'; ctx.fillText(text, x + 10, y + h / 2); }
      }
    };
    // Title row
    ctx.fillStyle = '#000'; ctx.fillRect(OX, OY, W, TITLE_H);
    ctx.fillStyle = '#fff'; ctx.font = '700 17px Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(`${site} ${titleTime} ${mmdd} Update`, OX + W / 2, OY + TITLE_H / 2);
    // Header row
    let y = OY + TITLE_H;
    drawCell(colX(0), y, COLS[0], HEAD_H, '#000', '#fff', '', 'left', true);
    ['Plan', 'Actual', '%'].forEach((t, i) => drawCell(colX(i + 1), y, COLS[i + 1], HEAD_H, '#000', '#fff', t, 'right', true));
    // Data rows
    y += HEAD_H;
    for (const d of disp) {
      drawCell(colX(0), y, COLS[0], ROW_H, '#000', '#fff', d.label, 'left', true);
      drawCell(colX(1), y, COLS[1], ROW_H, '#fff', '#000', d.plan, 'right', false);
      drawCell(colX(2), y, COLS[2], ROW_H, '#fff', '#000', d.actual, 'right', false);
      const pbg = d.pctColored ? (d.green ? '#c6efce' : '#ffc7ce') : '#fff';
      const pfg = d.pctColored ? (d.green ? '#1e6b2f' : '#9c1b2e') : '#000';
      drawCell(colX(3), y, COLS[3], ROW_H, pbg, pfg, d.pct, 'right', true);
      y += ROW_H;
    }
    // Export
    cv.toBlob(async (blob) => {
      if (!blob) { setCopyMsg('Copy failed'); return; }
      try {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        setCopyMsg('Copied to clipboard ✓');
      } catch (e) {
        // Fallback: download the PNG
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `quaterly_${site}_${date}_${tf}.png`; a.click();
        URL.revokeObjectURL(url);
        setCopyMsg('Clipboard blocked — downloaded PNG');
      }
      setTimeout(() => setCopyMsg(''), 4000);
    }, 'image/png');
  };

  return (
    <div>
      <h2 className="section-title">Quaterly — {site}</h2>
      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Timeframe</label>
            <select value={tf} onChange={e => setTf(e.target.value)}>
              {TIMEFRAMES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ justifyContent: 'flex-end', display: 'flex', gap: 8, alignItems: 'center' }}>
            {copyMsg && <span style={{ fontSize: '0.75rem', color: '#2e7d32' }}>{copyMsg}</span>}
            <button className="btn" onClick={copyTableImage} disabled={loading} style={{ background: '#455a64', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}>
              📋 Copy Image
            </button>
            <button className="btn btn-primary" onClick={fetchData} disabled={loading}>
              {loading ? 'Fetching...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ display: 'inline-block' }}>
        <table style={wrap}>
          <thead>
            <tr><td colSpan={4} style={titleCell}>{site} {titleTime} {mmdd} Update</td></tr>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}></th>
              <th style={th}>Plan</th>
              <th style={th}>Actual</th>
              <th style={{ ...th, paddingRight: 24 }}>%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const hasActual = r.actual != null && !r.pending;
              const hasPlan = r.plan != null;
              const pct = hasActual && hasPlan && r.plan !== 0 ? r.actual / r.plan : null;
              const green = pct != null && (r.lowerBetter ? pct <= 1 : pct >= 1);
              return (
                <tr key={r.label}>
                  <td style={labelCell}>{r.label}</td>
                  <td style={valCell}>{hasPlan ? r.fmt(r.plan) : '—'}</td>
                  <td style={valCell} title={r.pending || ''}>{hasActual ? r.fmt(r.actual) : (r.pending ? '⋯' : '—')}</td>
                  <td style={pct != null ? cellFor(green) : valCell}>{pct != null ? `${(pct * 100).toFixed(1)}%` : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!data?.hour_labels?.length && !loading && (
          <p style={{ fontSize: '0.8rem', color: '#666', marginTop: 8 }}>No FCLM data for this date. Try Refresh.</p>
        )}
      </div>

      {data?.hour_labels?.length > 0 && (
        <div className="card" style={{ display: 'inline-block', marginLeft: 16, verticalAlign: 'top' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr><td colSpan={2} style={{ background: '#232f3e', color: '#fff', fontWeight: 700, textAlign: 'center', padding: '8px 12px' }}>IB Carton Verification</td></tr>
              <tr><td colSpan={2} style={{ fontSize: '0.7rem', color: '#666', textAlign: 'center', padding: '2px 12px' }}>MonitorPortal, 00:00–{(TIMEFRAMES.find(t => t.key === tf) || {}).label.replace('0000-', '')}</td></tr>
            </thead>
            <tbody>
              {(() => {
                const mp = mpWindow?.[tf] || null;
                const pidV = mp ? mp.pid : null;
                const arosV = mp ? mp.aros : null;
                const prV = cartons('pr');
                const psV = cartons('ps');
                const totalIB = (mp ? (mp.pid || 0) + (mp.aros || 0) : null);
                const totalVal = totalIB != null ? totalIB + prV + psV : null;
                const rows = [
                  { label: 'PID Cartons (MP)', val: pidV },
                  { label: 'AROS Cartons (MP)', val: arosV },
                  { label: 'PR Cartons (FCLM)', val: prV },
                  { label: 'PS Cartons (FCLM)', val: psV },
                ];
                return (
                  <>
                    {rows.map(r => (
                      <tr key={r.label}>
                        <td style={{ border: '1px solid #ddd', padding: '5px 12px', fontWeight: 600 }}>{r.label}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px 12px', textAlign: 'right' }}>{r.val != null ? Math.round(r.val).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700, borderTop: '2px solid #232f3e' }}>
                      <td style={{ border: '1px solid #ddd', padding: '5px 12px' }}>Total IB</td>
                      <td style={{ border: '1px solid #ddd', padding: '5px 12px', textAlign: 'right' }}>{totalVal != null ? Math.round(totalVal).toLocaleString() : '—'}</td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
          {!mpWindow && <p style={{ fontSize: '0.7rem', color: '#c62828', marginTop: 6 }}>MonitorPortal window data not synced for this date yet.</p>}
        </div>
      )}
      <LineItemLLP api={api} site={site} date={date} data={data} endMin={(TIMEFRAMES.find(t => t.key === tf) || TIMEFRAMES[TIMEFRAMES.length - 1]).endMin} />
      <LLPUpdates api={api} site={site} date={date} />
    </div>
  );
}
